import { RiArrowLeftLine, RiEditLine, RiSaveLine, RiCloseLine } from "@remixicon/react";
import { Portal } from "./Portal";
import { useAssessment } from "@/hooks/useAssessment";
import { useToast } from "@/components/Toast";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { useState, useEffect } from "react";
import {
  createOptimizedPreviewFile,
  loadImage,
  needsImageOptimization,
} from "../utils/imageOptimization";
import { editorCommandButton } from "./EditImage/editorUi";

export function PreviewImage() {
  const { patient, previewImage, setPreviewImage, setEditImage, uploadImage } = useAssessment();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isAnimating, setIsAnimating] = useState(true);

  useEffect(() => {
    // Animação de entrada
    const timer = setTimeout(() => {
      setIsAnimating(false);
    }, 50);
    
    return () => clearTimeout(timer);
  }, []);

  const handleSave = async () => {
    if (!previewImage) {
      showToast({
        type: 'error',
        title: 'Erro!',
        message: 'Imagem não disponível para salvar.',
        position: 'top-right',
        duration: 5000,
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(previewImage.image);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      let fileToUpload: File;

      try {
        const image = await loadImage(objectUrl);
        const naturalWidth = image.naturalWidth || image.width;
        const naturalHeight = image.naturalHeight || image.height;
        const needsOptimization = needsImageOptimization(
          blob.size,
          naturalWidth,
          naturalHeight,
        );

        if (needsOptimization) {
          fileToUpload = await createOptimizedPreviewFile(image, previewImage.photoKey);
        } else {
          fileToUpload = new File([blob], `${previewImage.photoKey}-original.jpg`, {
            type: blob.type || "image/jpeg",
          });
        }
      } finally {
        URL.revokeObjectURL(objectUrl);
      }

      await uploadImage(fileToUpload, previewImage.photoKey);
      
      showToast({
        type: 'success',
        title: 'Sucesso!',
        message: 'Imagem salva com sucesso.',
        position: 'top-right',
        duration: 3000,
      });

      // Pequeno delay para o toast aparecer antes de fechar
      await new Promise(resolve => setTimeout(resolve, 400));

      // Fecha o preview
      setPreviewImage(undefined);
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Erro!',
        message: getErrorMessage(error, 'Falha ao salvar a imagem.'),
        position: 'top-right',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setPreviewImage(undefined);
  };

  const handleEditWithAnimation = () => {
    setIsTransitioning(true);
    
    // Aguarda a animação de saída (200ms)
    setTimeout(() => {
      setEditImage(previewImage);
      setPreviewImage(undefined);
    }, 200);
  };
  return (
    <Portal>
      <div className={`fixed inset-0 z-50 flex h-[100dvh] w-full flex-shrink-0 flex-col bg-[#f8fafc] transition-all duration-200 ease-in-out ${
        isTransitioning ? 'opacity-0 scale-95' : isAnimating ? 'opacity-0 scale-105' : 'opacity-100 scale-100'
      }`}>
        <header className="flex h-14 w-full shrink-0 items-center justify-between gap-3 border-b border-slate-200/80 bg-white px-3 lg:px-5">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-xl text-slate-600 transition-colors hover:bg-slate-50"
              onClick={() => {
                setPreviewImage(undefined);
              }}
            >
              <RiArrowLeftLine className="h-4 w-4 lg:h-5 lg:w-5" />
            </button>
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-600">Visualizando</p>
              <p className="truncate text-sm font-bold text-slate-900">{previewImage?.title || 'Nova imagem'}</p>
            </div>
          </div>
          {patient?.name && (
            <p className="max-w-[42%] truncate text-xs font-semibold text-slate-500">{patient.name}</p>
          )}
        </header>
        <div className="flex min-h-0 w-full flex-1 flex-col justify-center px-3 py-3">
          <div className="relative flex h-full w-full items-center justify-center">
            <img src={previewImage?.image} alt="image" className="max-h-full max-w-full object-contain" />
          </div>
        </div>
        <footer className="w-full shrink-0 border-t border-slate-200/80 bg-white px-4 py-3">
          <div className="flex items-center justify-center gap-1.5">
            <button
              type="button"
              onClick={handleEditWithAnimation}
              className={editorCommandButton('muted', false)}
            >
              <RiEditLine className="h-4 w-4 lg:h-5 lg:w-5" />
              <span>Editar</span>
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isLoading}
              className={editorCommandButton('primary', isLoading)}
            >
              <RiSaveLine className="h-4 w-4 lg:h-5 lg:w-5" />
              <span>{isLoading ? 'Salvando...' : 'Salvar'}</span>
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className={editorCommandButton('muted', false)}
            >
              <RiCloseLine className="h-4 w-4 lg:h-5 lg:w-5" />
              <span>Cancelar</span>
            </button>
          </div>
        </footer>
      </div>
    </Portal>
  );
}
